import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Store } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { ProductGrid } from "@/components/ProductGrid";
import { api, imageUrl, BEAUTY_CAT, FASHION_CAT } from "@/lib/lamka";
import { useLikeToggle } from "@/hooks/useLikeToggle";
import { rememberBrowse } from "@/lib/browse";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/context/AuthContext";
import { FollowButton } from "@/context/FollowsContext";

export default function Seller() {
  const { sellerId } = useParams();
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const onToggleLike = useLikeToggle(setItems);
  const name = seller?.shop_name || seller?.name || items[0]?.seller_name;
  usePageTitle(name || "Seller");

  useEffect(() => {
    setLoading(true);
    api.get(`/sellers/${sellerId}`).then((r) => setSeller(r.data)).catch(() => {});
    api.get("/listings", { params: { seller_id: sellerId } })
      .then((r) =>
        setItems(
          r.data
            .filter((l) => l.active !== false && [BEAUTY_CAT, FASHION_CAT].includes(l.category))
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        )
      )
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [sellerId, user?.user_id]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === "beauty" && it.category !== BEAUTY_CAT) return false;
      if (filter === "fashion" && it.category !== FASHION_CAT) return false;
      return !q || `${it.title} ${it.description || ""}`.toLowerCase().includes(q);
    });
  }, [items, filter, search]);
  useEffect(() => rememberBrowse(visible), [visible]);

  const logo = imageUrl(seller?.shop_logo);
  return (
    <div data-testid="seller-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar search={search} onSearch={setSearch} />
      <header className="bg-[#0F0F0F] text-white px-4 sm:px-8 lg:px-12 pt-36 lg:pt-40 pb-14 flex flex-col sm:flex-row sm:items-end gap-6">
        <div className="h-24 w-24 shrink-0 rounded-full overflow-hidden ring-1 ring-[#C5A059]/60 bg-[#141414] flex items-center justify-center">
          {logo ? <img data-testid="seller-logo" src={logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-8 w-8 text-[#C5A059]" strokeWidth={1} />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="eyebrow mb-3">The shop</p>
          <h1 data-testid="seller-name" className="font-display text-4xl sm:text-5xl leading-tight break-words">{name || "Lamka Online seller"}</h1>
          {!loading && <p data-testid="seller-count" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mt-3">{items.length} beauty & fashion {items.length === 1 ? "piece" : "pieces"}</p>}
        </div>
        <FollowButton sellerId={sellerId} name={name} className="self-start sm:self-end" />
      </header>
      <ProductGrid items={visible} loading={loading} error={error} filter={filter} setFilter={setFilter} search={search} onToggleLike={onToggleLike} eyebrow="Newest first" heading="All pieces" />
    </div>
  );
}
